import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import TPC from "../components/TPC";
import { ArrowRight, Check } from "lucide-react";

// Import all images
import OHtank from "../assets/images/project/OHTank.webp";
import PWS from "../assets/images/project/PumpWaterSupply.webp";
import IWS from "../assets/images/project/IWS.webp";
import GRPWS from "../assets/images/project/GRPWS.webp";
import USCS from "../assets/images/project/USCS.webp";
import RCCSC from "../assets/images/project/RCCSC.webp";
import PSS from "../assets/images/project/Pumping Sewage.webp";
import ChipwadLake from "../assets/images/project/LakePadra.webp";
import KL from "../assets/images/project/karjanlake.webp";
import ARR from "../assets/images/project/AsphaltRecarpet.webp";
import DHC from "../assets/images/project/dweeling.webp";
import GHS from "../assets/images/project/girlshostel.webp";
import RCCRoad from "../assets/images/project/cement-concrete-road.webp";
import Mangrol from "../assets/images/project/Mangrol.webp";
import SRTP from "../assets/images/project/solarrooftop.webp";
import SWDC from "../assets/images/SWDC.webp";
import WaterSupply from "../assets/images/WaterSupply.webp";
import UnderSewage from "../assets/images/undergroundsewage.webp";

// Services data
const services = [
  {
    title: "Water Supply",
    description: "Designing and implementing efficient water supply systems with cutting-edge technology for sustainable community development.",
  },
  {
    title: "Drainage",
    description: "Expert solutions in drainage system design and execution, ensuring effective water management and flood prevention.",
  },
  {
    title: "Sewage Treatment Plant",
    description: "Innovative and eco-friendly sewage treatment solutions using advanced technology for effective wastewater management.",
  },
  {
    title: "Storm Water",
    description: "Comprehensive stormwater management solutions with state-of-the-art planning and design to prevent flooding.",
  },
  {
    title: "Ecology",
    description: "Specialized in lake restoration and ecological development, promoting environmental balance and conservation.",
  },
  {
    title: "Building Projects",
    description: "Creating innovative and sustainable buildings that combine modern design with environmental responsibility.",
  },
  {
    title: "Road Projects",
    description: "End-to-end solutions for road infrastructure, from planning to maintenance, ensuring lasting quality.",
  },
  {
    title: "Fisheries",
    description: "Comprehensive fishery development services including modern pond construction and sustainable aquaculture.",
  },
  {
    title: "Solar Rooftop",
    description: "Advanced solar rooftop systems providing sustainable and cost-effective energy solutions for all sectors.",
  },
];

// Projects data
const allProjects = [
  {
    title: "Water and Sanitation Management Organisation",
    image: WaterSupply,
    service: "Water Supply",
  },
  {
    title: "Construction of Overhead Tank",
    image: OHtank,
    service: "Water Supply",
  },
  {
    title: "Construction Supervision Consultancy (CSC) Services",
    image: PWS,
    service: "Water Supply",
  },
  {
    title: "35 MGD Water Supply Project",
    image: IWS,
    service: "Water Supply",
  },
  {
    title: "Third Party Inspection",
    image: GRPWS,
    service: "Water Supply",
  },
  {
    title: "CSC-TPI Service",
    image: UnderSewage,
    service: "Drainage",
  },
  {
    title: "PMC for Construction",
    image: USCS,
    service: "Drainage",
  },
  {
    title: "TPI Services",
    image: RCCSC,
    service: "Drainage",
  },
  {
    title: "Drainage Gravity Line",
    image: PSS,
    service: "Sewage Treatment Plant",
  },
  {
    title: "TPI Services for Sewage Pumping Station",
    image: PSS,
    service: "Sewage Treatment Plant",
  },
  {
    title: "Sewerage Pumping Station",
    image: PSS,
    service: "Sewage Treatment Plant",
  },
  {
    title: "Project Management Consultancy",
    image: SWDC,
    service: "Storm Water",
  },
  {
    title: "Third Party Quality Assurance",
    image: SWDC,
    service: "Storm Water",
  },
  {
    title: "TPQA Work for Construction of Drainage Collection Network",
    image: WaterSupply,
    service: "Storm Water",
  },
  {
    title: "Third Party Inspection and Quality Assurance",
    image: Mangrol,
    service: "Fisheries",
  },
  {
    title: "TPI Services for Construction Work",
    image: ChipwadLake,
    service: "Ecology",
  },
  {
    title: "3 TPI Services for Development Work",
    image: ChipwadLake,
    service: "Ecology",
  },
  {
    title: "TPI Services for Development of Karjan Lake",
    image: KL,
    service: "Ecology",
  },
  {
    title: "Welding and Asphalt Recarpeting of Existing Road",
    image: DHC,
    service: "Building Projects",
  },
  {
    title: "Work of Yoga Pradarshani Bhavan and Girls Hostel",
    image: GHS,
    service: "Building Projects",
  },
  {
    title: "Work of Construction of Guest House and Library",
    image: GHS,
    service: "Building Projects",
  },
  {
    title: "Inspection of Material & Construction Work of Cement Concrete Road",
    image: ARR,
    service: "Road Projects",
  },
  {
    title: "Construction Work of Cement Concrete Road",
    image: ARR,
    service: "Road Projects",
  },
  {
    title: "TPI Services for Monitoring",
    image: ARR,
    service: "Road Projects",
  },
  {
    title: "TPI Services for Construction of RCC Road",
    image: RCCRoad,
    service: "Road Projects",
  },
  {
    title: "TPQA Work of Distribution Network with Tap Connection",
    image: SRTP,
    service: "Solar Rooftop",
  },
];

// Enhanced Animation Hook with more options
const useIntersectionObserver = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      });
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [hasAnimated]);

  return [ref, isVisible];
};

// Enhanced Animated Section with multiple animation types
const AnimatedSection = ({ children, className, animationType = "fade-up" }) => {
  const [ref, isVisible] = useIntersectionObserver();
  
  const animations = {
    'fade-up': 'opacity-0 translate-y-8',
    'fade-down': 'opacity-0 -translate-y-8',
    'fade-left': 'opacity-0 translate-x-8',
    'fade-right': 'opacity-0 -translate-x-8',
    'zoom-in': 'opacity-0 scale-95',
    'zoom-out': 'opacity-0 scale-105',
  };

  const activeClass = isVisible ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : animations[animationType];

  return (
    <div
      ref={ref}
      className={`${className} transform transition-all duration-700 ease-out ${activeClass}`}
    >
      {children}
    </div>
  );
};

// Enhanced Service Card with hover effects
const ServiceCard = ({ service, isSelected, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`w-full p-6 rounded-xl transition-all duration-500 border-2 group
        ${isSelected 
          ? "bg-[#87212e] text-white border-[#822e33] transform scale-102" 
          : "bg-white hover:bg-red-50/50 border-gray-200 hover:border-[#822e33]"
        }`}
    >
      <div className="flex flex-col h-full transform transition-transform duration-300">
        <div className="flex justify-between items-start mb-4">
          <h3 className={`text-xl font-bold transition-colors duration-300 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
            {service.title}
          </h3>
          <Check className={`w-5 h-5 transition-all duration-300 transform
            ${isSelected ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} 
          />
        </div>
        <p className={`text-sm leading-relaxed mb-4 transition-colors duration-300 
          ${isSelected ? 'text-white/90' : 'text-gray-600'}`}>
          {service.description}
        </p>
        <div className={`flex items-center mt-auto transition-colors duration-300
          ${isSelected ? 'text-white' : 'text-[#87212e]'}`}>
          <span className="text-sm font-medium">View projects</span>
          <ArrowRight className={`w-4 h-4 ml-2 transition-transform duration-300
            ${isHovered ? 'transform translate-x-1' : ''}`} 
          />
        </div>
      </div>
    </button>
  );
};

// Enhanced Project Card with advanced hover effects
const ProjectCard = ({ project, index }) => (
  <Link
    to={`/Project/${project.service.replace(/ /g, "")}/${encodeURIComponent(project.title)}`}
    className="block group h-full"
    style={{
      animationDelay: `${index * 100}ms`
    }}
  >
    <div className="bg-white h-full rounded-xl overflow-hidden shadow-md hover:shadow-xl 
      transition-all duration-500 flex flex-col transform hover:-translate-y-1">
      <div className="relative aspect-w-16 aspect-h-12 overflow-hidden">
        <img
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent 
          opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
      <div className="p-4 flex flex-col flex-grow relative z-10 bg-white transition-transform duration-500">
        <div className="mb-2 transform transition-all duration-500 group-hover:translate-y-0">
          <span className="inline-block px-3 py-1 text-xs font-medium text-[#822e33] bg-red-50 
            rounded-full transition-all duration-300 group-hover:bg-[#822e33] group-hover:text-white">
            {project.service}
          </span>
        </div>
        <h3 className="text-base font-semibold text-gray-900 group-hover:text-[#822e33] 
          transition-colors duration-300 line-clamp-2">
          {project.title}
        </h3>
      </div>
    </div>
  </Link>
);

// Main Component
const Service = () => {
  const [selectedService, setSelectedService] = useState("all");
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  useEffect(() => {
    setIsPageLoaded(true);
  }, []);

  const filteredProjects = selectedService === "all"
    ? allProjects
    : allProjects.filter((project) => project.service === selectedService);

  return (
    <main className={`w-full overflow-x-hidden bg-gray-50 transition-opacity duration-700
      ${isPageLoaded ? 'opacity-100' : 'opacity-0'}`}>
      {/* Hero Section with enhanced animation */}
      <section className="relative bg-gray-900 py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <AnimatedSection animationType="fade-down">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Engineering <span className="text-[#87212e] inline-block hover:scale-105 transition-transform duration-300">Excellence</span>
              </h1>
            </AnimatedSection>
            <AnimatedSection animationType="fade-up">
              <p className="text-lg text-gray-300">
                Delivering innovative solutions with a focus on quality and sustainability
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services Section with staggered animations */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection animationType="fade-down" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-gray-600">Comprehensive engineering solutions for your needs</p>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="col-span-full">
              <ServiceCard
                service={{
                  title: "All Services",
                  description: "Explore our complete portfolio of engineering solutions."
                }}
                isSelected={selectedService === "all"}
                onClick={() => setSelectedService("all")}
              />
            </div>
            {services.map((service, index) => (
              <AnimatedSection 
                key={index}
                animationType={index % 2 === 0 ? 'fade-left' : 'fade-right'}
                className="transition-transform duration-500 hover:scale-102"
              >
                <ServiceCard
                  service={service}
                  isSelected={selectedService === service.title}
                  onClick={() => setSelectedService(service.title)}
                />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section with grid animations */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimatedSection animationType="zoom-in" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Projects</h2>
            <p className="text-gray-600">
              {selectedService === "all"
                ? "Explore our diverse portfolio"
                : `${selectedService} projects`}
            </p>
          </AnimatedSection>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {filteredProjects.map((project, index) => (
              <AnimatedSection 
                key={index} 
                className="h-full"
                animationType={['fade-up', 'fade-left', 'fade-right'][index % 3]}
              >
                <ProjectCard project={project} index={index} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA with hover animation */}
      <section className="bg-[#87212e] py-16 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <AnimatedSection animationType="fade-up">
            <h2 className="text-2xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-white/90 mb-8">
              Let's work together to bring your vision to life.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 text-[#822e33] bg-white rounded-lg 
                hover:bg-gray-100 transition-all duration-300 hover:scale-105 group"
            >
              Contact Us
              <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 
                group-hover:translate-x-1" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <TPC />
    </main>
  );
};

export default Service;